import React, { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PaintBrush, CloudArrowUp, FloppyDisk, Image as ImageIcon, Trash, ArrowsClockwise, Eye, SealQuestion, Stack, Plus, Check, PencilSimple, Copy } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import MiniRichField from "@/components/MiniRichField";
import { openContractPdfPreview } from "@/lib/pdfPreview";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_DOC_STYLING = {
  header: { enabled: false, left: "", center: "", right: "", show_border: true },
  footer: { enabled: false, left: "", center: "Page &lt;n&gt; of &lt;total&gt;", right: "", show_border: false },
  watermark: { enabled: false, text: "DRAFT", opacity: 0.12, rotation: 30, color: "#9ca3af", font_size: 84 },
};

const HF_TOKENS = ["n", "total", "date", "title", "counterparty"];

export default function Branding() {
  const [branding, setBranding] = useState({});
  const [docStyling, setDocStyling] = useState(DEFAULT_DOC_STYLING);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sampleCid, setSampleCid] = useState(null);
  // Presets
  const [presets, setPresets] = useState([]);
  const [editingPreset, setEditingPreset] = useState(null); // id currently loaded into the form (null = tenant default)
  const [newPresetOpen, setNewPresetOpen] = useState(false);
  const [newPresetDraft, setNewPresetDraft] = useState({ name: "", description: "" });
  const [confirmPresetDelete, setConfirmPresetDelete] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/branding");
      setBranding(data || {});
      setDocStyling({
        header:    { ...DEFAULT_DOC_STYLING.header, ...(data?.doc_styling?.header || {}) },
        footer:    { ...DEFAULT_DOC_STYLING.footer, ...(data?.doc_styling?.footer || {}) },
        watermark: { ...DEFAULT_DOC_STYLING.watermark, ...(data?.doc_styling?.watermark || {}) },
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Pull the first contract id so the "Preview live PDF" button has a target
  useEffect(() => {
    api.get("/contracts").then(({ data }) => { if (data?.length) setSampleCid(data[0].id); }).catch(() => {});
  }, []);
  const loadPresets = () => api.get("/doc-presets").then(({ data }) => setPresets(data.presets || [])).catch(() => {});
  useEffect(() => { loadPresets(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editingPreset) {
        // Saving into the currently-loaded preset
        const p = presets.find((x) => x.id === editingPreset);
        const { data } = await api.patch(`/doc-presets/${editingPreset}`, {
          name: p?.name || "Untitled preset",
          description: p?.description || "",
          doc_styling: docStyling,
        });
        toast.success(`Preset "${data.preset.name}" updated`);
        loadPresets();
      } else {
        await api.patch("/tenant/branding", {
          primary_color: branding.primary_color,
          accent_color: branding.accent_color,
          terms_and_conditions: branding.terms_and_conditions,
          footer_text: branding.footer_text,
          company_address: branding.company_address,
          contact_email: branding.contact_email,
          doc_styling: docStyling,
        });
        toast.success("Tenant default branding saved");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const loadPresetIntoForm = (pid) => {
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    setDocStyling({
      header:    { ...DEFAULT_DOC_STYLING.header, ...(p.doc_styling?.header || {}) },
      footer:    { ...DEFAULT_DOC_STYLING.footer, ...(p.doc_styling?.footer || {}) },
      watermark: { ...DEFAULT_DOC_STYLING.watermark, ...(p.doc_styling?.watermark || {}) },
    });
    setEditingPreset(pid);
    toast.success(`Editing preset "${p.name}"`);
  };

  const loadTenantDefault = async () => {
    const { data } = await api.get("/tenant/branding");
    setDocStyling({
      header:    { ...DEFAULT_DOC_STYLING.header, ...(data?.doc_styling?.header || {}) },
      footer:    { ...DEFAULT_DOC_STYLING.footer, ...(data?.doc_styling?.footer || {}) },
      watermark: { ...DEFAULT_DOC_STYLING.watermark, ...(data?.doc_styling?.watermark || {}) },
    });
    setEditingPreset(null);
  };

  const createPreset = async () => {
    if (!newPresetDraft.name.trim()) { toast.error("Name is required"); return; }
    try {
      const { data } = await api.post("/doc-presets", {
        name: newPresetDraft.name.trim(),
        description: newPresetDraft.description,
        doc_styling: docStyling,
      });
      toast.success(`Preset "${data.preset.name}" created`);
      setNewPresetOpen(false);
      setNewPresetDraft({ name: "", description: "" });
      setEditingPreset(data.preset.id);
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const duplicatePreset = async (pid) => {
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    try {
      const { data } = await api.post("/doc-presets", {
        name: `${p.name} (copy)`,
        description: p.description,
        doc_styling: p.doc_styling,
      });
      toast.success(`Duplicated as "${data.preset.name}"`);
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };

  const deletePreset = async (pid) => {
    try {
      await api.delete(`/doc-presets/${pid}`);
      toast.success("Preset deleted");
      setConfirmPresetDelete(null);
      if (editingPreset === pid) loadTenantDefault();
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const previewLivePdf = async () => {
    if (!sampleCid) { toast.error("No contract to preview — create one first"); return; }
    await save();
    openContractPdfPreview(sampleCid);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    if (!file.type?.startsWith("image/")) { toast.error("Only image files are allowed"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tenant/branding/logo", fd);
      setBranding(prev => ({ ...prev, logo: data.logo }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteLogo = async () => {
    setConfirmDelete(false);
    try {
      await api.delete("/tenant/branding/logo");
      setBranding(prev => { const { logo, ...rest } = prev; void logo; return rest; });
      toast.success("Logo removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const logoUrl = (() => {
    const u = branding.logo?.url;
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${BACKEND}${u.startsWith("/") ? "" : "/"}${u}`;
  })();

  const setHf = (section, k, v) => setDocStyling((prev) => ({ ...prev, [section]: { ...prev[section], [k]: v } }));
  const setWm = (k, v) => setDocStyling((prev) => ({ ...prev, watermark: { ...prev.watermark, [k]: v } }));

  return (
    <PageShell
      title="Company Branding"
      subtitle="Logo, colours, header/footer & watermark applied to every generated contract PDF"
      right={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={previewLivePdf} disabled={!sampleCid || saving} data-testid="branding-preview-btn" className="gap-2">
            <Eye size={14} /> Preview live PDF
          </Button>
          <Button onClick={save} disabled={saving} data-testid="branding-save-btn" className="gap-2">
            <FloppyDisk size={14} /> {saving ? "Saving…" : (editingPreset ? "Save to preset" : "Save")}
          </Button>
        </div>
      }
    >
      <Tabs defaultValue="identity">
        <TabsList className="mb-4">
          <TabsTrigger value="identity" data-testid="tab-identity"><PaintBrush size={12} className="mr-1.5" /> Identity</TabsTrigger>
          <TabsTrigger value="layout" data-testid="tab-layout"><SealQuestion size={12} className="mr-1.5" /> Document Layout</TabsTrigger>
        </TabsList>

        <TabsContent value="identity">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <GlassCard className="p-6 lg:col-span-1">
              <div className="overline mb-3">Logo</div>
              <div className="h-40 border border-dashed border-[var(--border-strong)] rounded-md flex items-center justify-center bg-[var(--surface)] mb-3 overflow-hidden relative">
                {logoUrl ? (
                  <img src={logoUrl} alt="Tenant logo" data-testid="branding-logo-img" onError={() => toast.error("Logo failed to load — try re-uploading")} className="max-h-full max-w-full object-contain" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-[var(--text-3)]">
                    <ImageIcon size={28} /><span className="text-[11px]">No logo uploaded</span>
                  </div>
                )}
                {uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-xs text-white">Uploading…</div>}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="branding-logo-input" />
              {logoUrl ? (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-logo-replace-btn" className="flex-1 gap-2">
                    <ArrowsClockwise size={14} /> Replace
                  </Button>
                  <Button variant="outline" onClick={() => setConfirmDelete(true)} disabled={uploading} data-testid="branding-logo-delete-btn" className="gap-2 text-[var(--danger)] hover:text-[var(--danger)]">
                    <Trash size={14} /> Remove
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-logo-upload-btn" className="w-full gap-2">
                  <CloudArrowUp size={14} /> Upload logo
                </Button>
              )}
              <div className="text-[11px] text-[var(--text-3)] mt-2">PNG, JPG, SVG or WebP. Ideally 400×120, under 2MB.</div>
            </GlassCard>

            <GlassCard className="p-6 lg:col-span-2">
              <div className="overline mb-3">Colours & typography</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Primary colour</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <input type="color" value={branding.primary_color || "#0B0D12"} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} data-testid="branding-primary-color" className="w-12 h-10 rounded border border-[var(--border)] bg-transparent cursor-pointer" />
                    <Input value={branding.primary_color || ""} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} placeholder="#0B0D12" className="font-mono text-xs glass-subtle border-[var(--border)]" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Accent colour</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <input type="color" value={branding.accent_color || "#38BDF8"} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })} data-testid="branding-accent-color" className="w-12 h-10 rounded border border-[var(--border)] bg-transparent cursor-pointer" />
                    <Input value={branding.accent_color || ""} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })} placeholder="#38BDF8" className="font-mono text-xs glass-subtle border-[var(--border)]" />
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="p-6 lg:col-span-3">
              <div className="overline mb-3">Terms & Conditions (appended to every PDF)</div>
              <Textarea rows={8} value={branding.terms_and_conditions || ""} onChange={(e) => setBranding({ ...branding, terms_and_conditions: e.target.value })} placeholder="E.g. Any amendments require written agreement signed by both parties…" data-testid="branding-tnc" className="glass-subtle border-[var(--border)] text-sm" />
            </GlassCard>

            <GlassCard className="p-6 lg:col-span-3 grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Legacy footer text (only rendered when the styled footer below is OFF)</Label>
                <Textarea rows={3} value={branding.footer_text || ""} onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })} placeholder="Clariva Workspace · 123 Main St · www.clariva.ai" data-testid="branding-footer" className="mt-1.5 glass-subtle border-[var(--border)] text-xs" />
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Contact email</Label>
                  <Input value={branding.contact_email || ""} onChange={(e) => setBranding({ ...branding, contact_email: e.target.value })} placeholder="legal@company.com" data-testid="branding-email" className="mt-1.5 glass-subtle border-[var(--border)]" />
                </div>
                <div>
                  <Label className="text-xs">Company address</Label>
                  <Input value={branding.company_address || ""} onChange={(e) => setBranding({ ...branding, company_address: e.target.value })} placeholder="Suite 401, 123 Main Street" data-testid="branding-address" className="mt-1.5 glass-subtle border-[var(--border)]" />
                </div>
              </div>
            </GlassCard>
          </div>
        </TabsContent>

        <TabsContent value="layout" className="space-y-5">
          <GlassCard className="p-5" data-testid="presets-panel">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <Stack size={16} weight="duotone" className="text-[var(--accent)]" />
                  <h3 className="text-base font-medium" style={{ fontFamily: "var(--font-heading)" }}>Saved presets</h3>
                  <span className="chip chip-neutral text-[10px]">{presets.length}</span>
                </div>
                <div className="text-[11px] text-[var(--text-3)] mt-1">
                  Save named styling presets (e.g., "NDA · Confidential", "Letterhead · Formal") and map each one to a document type in
                  <code className="px-1 mx-1 font-mono text-[var(--accent)]">Doc Types → Workflows</code>.
                  Every contract auto-picks the preset for its type at render time.
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 pe-2 border-r border-[var(--border)]">
                  <span className="text-[11px] text-[var(--text-3)]">Editing:</span>
                  <Select value={editingPreset || "__default__"} onValueChange={(v) => { if (v === "__default__") loadTenantDefault(); else loadPresetIntoForm(v); }}>
                    <SelectTrigger className="h-8 min-w-[200px] glass-subtle border-[var(--border)]" data-testid="presets-load-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">Tenant default styling</SelectItem>
                      {presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" variant="outline" onClick={() => setNewPresetOpen(true)} data-testid="preset-new-btn" className="gap-2">
                  <Plus size={13} /> Save as new preset
                </Button>
                {editingPreset && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => duplicatePreset(editingPreset)} data-testid="preset-duplicate-btn" className="gap-2">
                      <Copy size={13} /> Duplicate
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmPresetDelete(editingPreset)} data-testid="preset-delete-btn" className="gap-2 text-[var(--danger)] hover:text-[var(--danger)]">
                      <Trash size={13} /> Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-3)] pt-2 border-t border-[var(--border)]" data-testid="presets-current-target">
              Current target:
              <span className="ms-1 font-semibold text-[var(--accent)]">
                {editingPreset ? (presets.find((p) => p.id === editingPreset)?.name || "preset") : "Tenant default"}
              </span>
              {editingPreset && (() => {
                const uc = presets.find((p) => p.id === editingPreset)?.usage_count;
                return typeof uc === "number" ? (
                  <span className="ms-2 normal-case tracking-normal text-[11px] text-[var(--text-2)]">
                    · used by {uc} doc-type{uc === 1 ? "" : "s"}
                  </span>
                ) : null;
              })()}
              <span className="ms-2 text-[var(--text-3)]">— Save button below writes to this target.</span>
            </div>
          </GlassCard>

          <div className="text-xs text-[var(--text-3)] -mt-1 mb-1">
            Insert tokens to dynamically substitute values at render time:
            {HF_TOKENS.map((t) => <code key={t} className="mx-1 font-mono text-[var(--accent)]">&lt;{t}&gt;</code>)}
            — <span className="text-[var(--text-2)]">{`<n>`}</span> = current page · <span className="text-[var(--text-2)]">{`<total>`}</span> = total pages
          </div>

          <SectionCard
            title="Header"
            testId="layout-header"
            enabled={docStyling.header.enabled}
            onToggle={(v) => setHf("header", "enabled", v)}
            extra={
              <label className="flex items-center gap-2 text-[11px] text-[var(--text-2)]">
                <Switch checked={docStyling.header.show_border} onCheckedChange={(v) => setHf("header", "show_border", v)} data-testid="header-border-switch" /> Show border line
              </label>
            }
          >
            <HFTriplet section="header" block={docStyling.header} setHf={setHf} />
          </SectionCard>

          <SectionCard
            title="Footer"
            testId="layout-footer"
            enabled={docStyling.footer.enabled}
            onToggle={(v) => setHf("footer", "enabled", v)}
            extra={
              <label className="flex items-center gap-2 text-[11px] text-[var(--text-2)]">
                <Switch checked={docStyling.footer.show_border} onCheckedChange={(v) => setHf("footer", "show_border", v)} data-testid="footer-border-switch" /> Show border line
              </label>
            }
          >
            <HFTriplet section="footer" block={docStyling.footer} setHf={setHf} />
          </SectionCard>

          <SectionCard
            title="Watermark"
            testId="layout-watermark"
            enabled={docStyling.watermark.enabled}
            onToggle={(v) => setWm("enabled", v)}
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Watermark text</Label>
                <Input value={docStyling.watermark.text} onChange={(e) => setWm("text", e.target.value)} placeholder="DRAFT · CONFIDENTIAL · SPECIMEN" data-testid="watermark-text" className="mt-1.5 glass-subtle border-[var(--border)] uppercase tracking-widest font-semibold" />
              </div>
              <div>
                <Label className="text-xs">Colour</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="color" value={docStyling.watermark.color} onChange={(e) => setWm("color", e.target.value)} data-testid="watermark-color" className="w-12 h-10 rounded border border-[var(--border)] bg-transparent cursor-pointer" />
                  <Input value={docStyling.watermark.color} onChange={(e) => setWm("color", e.target.value)} className="font-mono text-xs glass-subtle border-[var(--border)]" />
                </div>
              </div>
              <div>
                <Label className="text-xs flex items-center justify-between">Opacity <span className="font-mono text-[var(--text-3)]">{Math.round(docStyling.watermark.opacity * 100)}%</span></Label>
                <Slider value={[docStyling.watermark.opacity * 100]} min={2} max={60} step={1} onValueChange={([v]) => setWm("opacity", v / 100)} data-testid="watermark-opacity" className="mt-3" />
              </div>
              <div>
                <Label className="text-xs flex items-center justify-between">Rotation <span className="font-mono text-[var(--text-3)]">{Math.round(docStyling.watermark.rotation)}°</span></Label>
                <Slider value={[docStyling.watermark.rotation]} min={-90} max={90} step={1} onValueChange={([v]) => setWm("rotation", v)} data-testid="watermark-rotation" className="mt-3" />
              </div>
              <div>
                <Label className="text-xs flex items-center justify-between">Font size <span className="font-mono text-[var(--text-3)]">{docStyling.watermark.font_size}pt</span></Label>
                <Slider value={[docStyling.watermark.font_size]} min={24} max={240} step={2} onValueChange={([v]) => setWm("font_size", v)} data-testid="watermark-fontsize" className="mt-3" />
              </div>
              <div className="flex items-end">
                <div className="w-full h-[70px] rounded-md border border-[var(--border)] bg-[var(--surface)] flex items-center justify-center relative overflow-hidden" data-testid="watermark-live-preview">
                  <span
                    data-testid="watermark-live-preview-text"
                    className="select-none whitespace-nowrap font-bold uppercase tracking-widest"
                    style={{
                      color: docStyling.watermark.color,
                      opacity: docStyling.watermark.opacity,
                      transform: `rotate(${docStyling.watermark.rotation}deg)`,
                      fontSize: Math.max(12, Math.min(40, docStyling.watermark.font_size / 3)),
                    }}
                  >
                    {docStyling.watermark.text || "WATERMARK"}
                  </span>
                </div>
              </div>
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Remove tenant logo?"
        description="Your logo will disappear from the sidebar, PDF previews, email headers and external signatory inbox until you upload a new one."
        actionLabel="Remove logo"
        destructive
        onConfirm={deleteLogo}
        testId="logo-delete-dialog"
      />

      <ConfirmDialog
        open={!!confirmPresetDelete}
        onOpenChange={(v) => !v && setConfirmPresetDelete(null)}
        title="Delete this preset?"
        description={(() => {
          const p = presets.find((x) => x.id === confirmPresetDelete);
          const uc = p?.usage_count || 0;
          return uc > 0
            ? `This preset is currently used by ${uc} document-type mapping${uc === 1 ? "" : "s"} — those will revert to the tenant default styling.`
            : "Any document-type mappings using this preset will automatically revert to the tenant default styling.";
        })()}
        actionLabel="Delete preset"
        destructive
        onConfirm={() => confirmPresetDelete && deletePreset(confirmPresetDelete)}
        testId="preset-delete-dialog"
      />

      <Dialog open={newPresetOpen} onOpenChange={setNewPresetOpen}>
        <DialogContent data-testid="new-preset-dialog">
          <DialogHeader>
            <DialogTitle>Save as new preset</DialogTitle>
            <DialogDescription>Snapshots the current header / footer / watermark configuration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={newPresetDraft.name}
                onChange={(e) => setNewPresetDraft({ ...newPresetDraft, name: e.target.value })}
                placeholder="e.g. NDA · Confidential"
                data-testid="new-preset-name"
                className="mt-1.5 glass-subtle border-[var(--border)]"
              />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea
                rows={2}
                value={newPresetDraft.description}
                onChange={(e) => setNewPresetDraft({ ...newPresetDraft, description: e.target.value })}
                placeholder="When to use this preset…"
                data-testid="new-preset-description"
                className="mt-1.5 glass-subtle border-[var(--border)] text-sm"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setNewPresetOpen(false)}>Cancel</Button>
            <Button onClick={createPreset} data-testid="new-preset-create-btn" className="gap-2"><Check size={13} /> Create preset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

function SectionCard({ title, testId, enabled, onToggle, extra, children }) {
  return (
    <GlassCard className="p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`${testId}-enable`} />
          <h3 className="text-base font-medium" style={{ fontFamily: "var(--font-heading)" }}>{title}</h3>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${enabled ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-[var(--surface)] text-[var(--text-3)] border border-[var(--border)]"}`}>{enabled ? "Enabled" : "Disabled"}</span>
        </div>
        {extra}
      </div>
      <div className={`${enabled ? "opacity-100" : "opacity-55 pointer-events-none"} transition-opacity`}>{children}</div>
    </GlassCard>
  );
}

function HFTriplet({ section, block, setHf }) {
  return (
    <div className="grid md:grid-cols-3 gap-3">
      {["left", "center", "right"].map((col) => (
        <div key={col}>
          <Label className="text-[11px] uppercase tracking-wider text-[var(--text-3)]">{col}</Label>
          <div className="mt-1.5">
            <MiniRichField
              value={block[col]}
              onChange={(v) => setHf(section, col, v)}
              placeholder={col === "center" && section === "footer" ? "e.g. Page <n> of <total>" : `${section} ${col}…`}
              tokens={HF_TOKENS}
              testId={`${section}-${col}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
